import React, { useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import LegalLinks from './LegalLinks';
import { useLanguage } from '../i18n';
import { standardErrorMessage } from '../utils/userError';

// Password Recovery (#338) — standard "Forgot Password" flow.
//
// Firebase Authentication owns the reset mechanics end to end: a secure,
// time-limited, single-use link is minted and emailed by
// `sendPasswordResetEmail`, and the "set a new password" interface itself is
// Firebase's own hosted action page (reached via the link in that email) — it
// enforces the link's expiry and one-time-use, so none of that is rebuilt here.
// This page is only the email-REQUEST step.
//
// Security-first messaging (anti-enumeration): whether the email belongs to a
// real account or not, the user sees the EXACT same confirmation. A genuinely
// malformed address is a client-side validation error (never sent to Firebase
// at all — there is nothing to enumerate from a syntax check), and a true
// operational failure (offline, rate-limited) is still reported honestly, since
// masking those as "email sent" would leave the user stuck with no signal.
//
// 2FA: this app has no second factor today, so "still challenge 2FA on next
// login" is vacuously satisfied — there is no second factor to challenge. If
// MFA is added later, it lives entirely on the LOGIN path and needs no change
// here.
const EMAIL_RE = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;

const ForgotPasswordPage = ({ onResetPassword }) => {
    const { t } = useLanguage();
    const [email, setEmail] = useState('');
    const [error, setError] = useState('');
    const [loading, setLoading] = useState(false);
    const [submitted, setSubmitted] = useState(false);
    const navigate = useNavigate();

    const loginHref = '/login';

    const handleSubmit = async (e) => {
        e.preventDefault();
        setError('');

        const trimmed = email.trim();
        if (!EMAIL_RE.test(trimmed)) {
            setError(t('auxForgotPasswordInvalidEmail', 'Please enter a valid email address.'));
            return;
        }

        setLoading(true);
        try {
            const result = await onResetPassword(trimmed);
            if (result.success || result.code === 'auth/user-not-found') {
                // Anti-enumeration: an existing account and a non-existent one
                // render IDENTICALLY from here on.
                setSubmitted(true);
                return;
            }
            switch (result.code) {
                case 'auth/invalid-email':
                    setError(t('auxForgotPasswordInvalidEmail', 'Please enter a valid email address.'));
                    break;
                case 'auth/too-many-requests':
                    setError(t('authTooManyAttempts', 'Too many failed attempts. Please try again later.'));
                    break;
                default:
                    setError(standardErrorMessage(t));   // #439
            }
        } catch (err) {
            setError(standardErrorMessage(t));
        } finally {
            setLoading(false);
        }
    };

    return (
        <div className="relative flex h-dvh overflow-y-auto bg-pai-void text-white">
            <div
                aria-hidden="true"
                className="fixed inset-0 bg-cover bg-center"
                style={{ backgroundImage: "url('/wallpaper.jpg')" }}
            />
            <div aria-hidden="true" className="fixed inset-0 bg-[rgba(5,7,10,0.64)]" />
            {/* #432: `m-auto` centres the card while it fits and lets the page
                scroll when it does not (a phone held sideways, a short window).
                `items-center` on the parent used to centre it OUTSIDE the
                viewport instead, cutting off the top and the submit button. */}
            <div className="relative w-full max-w-md px-6 py-6 m-auto">
                <div className="w-full p-8 space-y-6 rounded-modal border border-white/[0.14] bg-white/[0.03] backdrop-blur-tile">
                    <div className="text-center">
                        <h2 className="text-[28px] font-extrabold tracking-[-0.025em] text-white">
                            {t('auxForgotPasswordTitle', 'Reset your password')}
                        </h2>
                        {!submitted && (
                            <p className="mt-2 text-sm text-pai-meta">
                                {t('auxForgotPasswordSubtitle', "Enter your email address and we'll send you a link to reset your password.")}
                            </p>
                        )}
                    </div>

                    {submitted ? (
                        <div className="space-y-6">
                            <div
                                data-testid="forgot-password-confirmation"
                                className="p-4 text-sm text-center text-white border border-white/[0.14] bg-white/[0.05] rounded-ctl"
                                role="status"
                            >
                                {t('auxForgotPasswordConfirmation', 'If an account exists for this email, a reset link has been sent.')}
                            </div>
                            {/* #375 — the one thing a user who sees nothing can
                                actually act on. Its own element, below the
                                neutral confirmation, so the anti-enumeration
                                sentence above stays byte-identical for an
                                existing and a non-existent account. */}
                            <p
                                data-testid="forgot-password-spam-hint"
                                className="text-sm text-center text-pai-meta"
                            >
                                {t('auxForgotPasswordSpamHint', 'If you do not see the email in your inbox within a few minutes, please check your spam folder.')}
                            </p>
                            <button
                                type="button"
                                data-testid="forgot-password-back-to-login"
                                onClick={() => navigate(loginHref)}
                                className="w-full h-11 px-4 text-sm font-semibold text-pai-void bg-white rounded-ctl hover:bg-white/90 transition-colors focus:outline-none"
                            >
                                {t('auxForgotPasswordBackToLogin', 'Back to login')}
                            </button>
                        </div>
                    ) : (
                        <>
                            {error && (
                                <div
                                    data-testid="forgot-password-error"
                                    className="p-4 text-sm text-pai-red border border-[rgba(240,68,56,0.4)] bg-[rgba(240,68,56,0.08)] rounded-ctl"
                                    role="alert"
                                >
                                    {error}
                                </div>
                            )}

                            <form className="space-y-6" onSubmit={handleSubmit} noValidate>
                                <div>
                                    <label htmlFor="email" className="block text-sm font-medium text-pai-secondary">
                                        {t('auxEmailAddress', 'Email address')}
                                    </label>
                                    <input
                                        id="email"
                                        name="email"
                                        type="email"
                                        autoComplete="email"
                                        data-testid="forgot-password-email"
                                        value={email}
                                        onChange={(e) => setEmail(e.target.value)}
                                        className="block w-full h-11 px-3 mt-1 pai-input appearance-none focus:outline-none text-sm"
                                    />
                                </div>
                                <button
                                    type="submit"
                                    disabled={loading}
                                    data-testid="forgot-password-submit"
                                    className={`w-full h-11 px-4 text-sm font-semibold rounded-ctl transition-colors focus:outline-none ${
                                        loading
                                            ? 'bg-white/40 text-pai-void cursor-not-allowed'
                                            : 'bg-white text-pai-void hover:bg-white/90'
                                    }`}
                                >
                                    {loading
                                        ? t('auxForgotPasswordSending', 'Sending...')
                                        : t('auxForgotPasswordSubmit', 'Send reset link')}
                                </button>
                            </form>

                            <p className="text-sm text-center text-pai-meta">
                                <Link
                                    to={loginHref}
                                    data-testid="forgot-password-back-link"
                                    className="font-semibold text-pai-blue hover:text-pai-blue-tint"
                                >
                                    {t('auxForgotPasswordBackToLogin', 'Back to login')}
                                </Link>
                            </p>
                        </>
                    )}
                </div>
                <LegalLinks className="mt-6" />
            </div>
        </div>
    );
};

export default ForgotPasswordPage;
